import {skipToken} from '@reduxjs/toolkit/query';
import {Box} from 'platform/foundation';
import {v4 as uuidv4} from 'uuid';

import {ReactElement, useCallback, useState} from 'react';
import {useField} from 'react-final-form';

import {isNil, isNotNil, pluck} from 'ramda';
import {compact} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useAddFilesToVehicleAlbumMutation,
  useGetVehicleQuery,
  useVehiclePhotoDeleteMutation,
  useVehiclePhotoUpdateAlbumMutation,
} from '@dms/api/sales';
import {ApiError, EntityResourceIds, VehicleAlbums} from '@dms/api/shared';
import {testIds} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {UploadedFile} from '../../../services/UploadedFile';
import {CreateVehicleRequestBody} from '../../../types/CreateVehicleRequestBody';
import {filterSortImageByWeight} from '../../../utils/filterSortImageByWeight';
import {DraggableDataWithContextId} from '../../CustomDragAndDrop/utils/types';
import {useFormRenderer} from '../../FinalForm/hooks/useFormRenderer';
import {ImageType} from '../../ImagesCard/utils/types';
import {UploadPhotos} from './UploadPhotos';

interface PhotosTabProps {
  vehicleId?: string;
}

let uploaderChanged = false;

export const PhotosTab = (props: PhotosTabProps): ReactElement => {
  const {data: vehicleParticipation} = useGetParticipationQuery(
    isNotNil(props.vehicleId)
      ? {
          resourceId: EntityResourceIds.vehicle,
          recordId: props.vehicleId,
        }
      : skipToken
  );

  const {data: vehicle} = useGetVehicleQuery(
    isNotNil(props.vehicleId) ? {vehicleId: props.vehicleId} : skipToken
  );

  const [canUploadVehicleAlbumPhotos, canDeleteVehicleAlbumPhotos] = usePermissions({
    permissionKeys: ['createVehicleAlbumPhotos', 'deleteVehicleAlbumPhoto'],
    scopes: {
      createVehicleAlbumPhotos: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      deleteVehicleAlbumPhoto: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {input} = useField('photos');
  const {Field} = useFormRenderer<CreateVehicleRequestBody>();
  const [photos, setPhotos] = useState<ImageType[]>(input.value);

  const [addFilesToVehicleAlbum] = useAddFilesToVehicleAlbumMutation();
  const [vehiclePhotoDelete] = useVehiclePhotoDeleteMutation();
  const [vehiclePhotoUpdateAlbum] = useVehiclePhotoUpdateAlbumMutation();

  const getSalesVehicleAlbum = () => {
    const salesAlbumId = vehicle?.albums?.find(
      (a) => a?.name === VehicleAlbums.PHOTOS_CAR_PURCHASE
    )?.id;

    return salesAlbumId || '';
  };

  const getPhotos = (): ImageType[] => {
    if (props.vehicleId) {
      const salesAlbumId = getSalesVehicleAlbum();
      return filterSortImageByWeight(vehicle?.photos || [], salesAlbumId);
    }

    return photos;
  };

  const handleDragEnd = (
    items: DraggableDataWithContextId<ImageType>[],
    position: number,
    drop?: DraggableDataWithContextId<ImageType>
  ) => {
    const ids = items.map((i) => i.data?.id);
    const newImages: ImageType[] = [];

    if (props.vehicleId) {
      const salesAlbum = getSalesVehicleAlbum();
      const dropWeight = drop?.data?.weight;
      const resolvedPosition = isNil(dropWeight) ? null : Number(dropWeight);

      return vehiclePhotoUpdateAlbum({
        vehicleId: props.vehicleId,
        vehiclePhotoUpdateAlbumRequestBody: {
          sourceAlbum: salesAlbum,
          destinationAlbum: salesAlbum,
          photos: ids.map((id, i) => ({fileId: id, position: i})),
          position: resolvedPosition,
        },
      })
        .unwrap()
        .catch(handleApiError);
    } else {
      photos.forEach((p, i) => {
        const found = ids.find((i) => i === p.id);

        if (i === position) {
          newImages.push(...items.map((i) => ({...i.data})));
        }

        if (!found) {
          newImages.push(p);
        }
      });

      if (position >= photos.length) {
        newImages.push(...items.map((i) => ({...i.data})));
      }

      setPhotos(newImages.map((i) => ({...i, isSelected: undefined})));
    }
  };

  const handlePhotoDeletion = (ids: string[]) => {
    if (!canDeleteVehicleAlbumPhotos) {
      return;
    }
    if (props.vehicleId) {
      return vehiclePhotoDelete({
        vehicleId: props.vehicleId,
        albumId: getSalesVehicleAlbum(),
        vehiclePhotoDeleteRequestBody: {
          photos: ids.map((id) => ({fileId: id})),
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    const _photos = [...photos];

    ids?.forEach((i) => {
      const foundPhoto = _photos.findIndex((p) => p.id === i);

      if (foundPhoto !== -1) {
        _photos.splice(foundPhoto, 1);
      }
    });

    setPhotos(_photos);
  };

  const handleFileChange = useCallback(
    async (files: UploadedFile[]) => {
      uploaderChanged = true;

      if (props.vehicleId) {
        const salesAlbumId = vehicle?.albums?.find(
          (a) => a?.name === VehicleAlbums.PHOTOS_CAR_PURCHASE
        )?.id;
        const fileIds = compact(pluck('fileId', files));

        if (salesAlbumId && fileIds.length) {
          try {
            await addFilesToVehicleAlbum({
              vehicleId: props.vehicleId,
              vehicleAlbumId: salesAlbumId,
              addFilesToVehicleAlbumRequestBody: {fileIds},
            }).unwrap();
          } catch (error) {
            handleApiError(error as ApiError);
          }
        }
      } else {
        const filesWithId: ImageType[] = files.map((file) => ({...file, id: uuidv4()}));
        setPhotos(input.value ? [...input.value, ...filesWithId] : filesWithId);
      }
    },
    [addFilesToVehicleAlbum, input.value, props.vehicleId, vehicle]
  );

  return (
    <>
      <Box>
        <Field
          name="photos"
          component={({input}) => {
            if (!props.vehicleId && uploaderChanged) {
              input.onChange(getPhotos());
            }

            return (
              <UploadPhotos
                album={
                  props.vehicleId
                    ? vehicle?.albums?.find((a) => a.name === VehicleAlbums.PHOTOS_CAR_PURCHASE)
                    : undefined
                }
                images={getPhotos()}
                isAddForm={!props.vehicleId && canUploadVehicleAlbumPhotos}
                isUploadDisabled={!canUploadVehicleAlbumPhotos}
                isDeleteDisabled={!canDeleteVehicleAlbumPhotos}
                onDelete={handlePhotoDeletion}
                onChange={handleFileChange}
                onDragEnd={handleDragEnd}
                data-testid={testIds.vehicles.create('photos')}
              />
            );
          }}
        />
      </Box>
    </>
  );
};
